/*
 * Copyright 2018 - 2021 Swiss Federal Institute of Technology Lausanne (EPFL)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0.
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * This open source software code was developed in part or in whole in the
 * Human Brain Project, funded from the European Union's Horizon 2020
 * Framework Programme for Research and Innovation under
 * Specific Grant Agreements No. 720270, No. 785907, and No. 945539
 * (Human Brain Project SGA1, SGA2 and SGA3).
 *
 */

import React from "react";
import Button from "react-bootstrap/Button";

import useInstanceInitializationQuery from "../hooks/useGetInstanceInitializationQuery";
import Fetching from "./Fetching";
import Error from "./Error";
import ContactText from "./ContactText";
import GoToInstance from "./GoToInstance";

interface InstanceCreationProps {
  space: string;
  schemas: string[];
  instanceId: string;
}

const InstanceCreation = ({ space, schemas, instanceId }: InstanceCreationProps) => {

  const {
    error,
    isUninitialized,
    isFetching,
    isSuccess,
    isError,
    refetch,
  } = useInstanceInitializationQuery(space, schemas, instanceId);

  if (isError) {
    return (
        <Error>
          <h3>An error occured while creating instance &quot;{instanceId}&quot;.</h3>
          <i>{error}</i><br /><br />
          <ContactText />
          <div><Button variant="primary" onClick={refetch}>Retry</Button></div>
        </Error>
    );
  }

  if(isUninitialized || isFetching) {
    return (
      <Fetching text={`Creating instance "${instanceId}"...`} />
    );
  }

  if (isSuccess) {
    return (
      <GoToInstance instanceId={instanceId} canEdit={true} />
    );
  }

  return null;
};

export default InstanceCreation;